/**
 * Mengundang orang LANGSUNG dari Workforce — bagian MURNI.
 *
 * ADA KARENA keberatan Owner 19 Agustus 2026: "kalau bukan marketing, kenapa harus lewat CRM
 * dulu — CRM kita peruntukkan untuk marketing."
 *
 * Keberatan itu tepat, dan batasnya lebih kecil dari yang terlihat. Yang WAJIB bukan CRM,
 * melainkan IDENTITAS HBS — akun dan keanggotaan organisasi. Itu milik HBS core, dan Workforce
 * memang tidak boleh membuat akun sendiri: akun kedua untuk orang yang sama memecah identitasnya
 * menjadi dua yang tidak pernah bertemu.
 *
 * Yang membuat CRM terasa wajib hanyalah satu hal: SATU-SATUNYA LAYAR UNDANGAN kebetulan ada di
 * sana. Itu celah tampilan, bukan syarat arsitektur — dan celah tampilan dapat ditutup dari sini
 * tanpa Workforce belajar membuat akun.
 *
 * Jalur ini memanggil endpoint undangan HBS, pola yang SUDAH dipakai Workforce untuk
 * `GET /me/memberships` di layar masuk.
 */

/** Peran keanggotaan HBS — kasar, menentukan kuasa atas ORGANISASI, bukan pekerjaan lapangan. */
export type MembershipRole = 'member' | 'admin';

export const MEMBERSHIP_ROLE_CHOICES: readonly {
  readonly value: MembershipRole;
  readonly label: string;
  readonly hint: string;
}[] = [
  {
    value: 'member',
    label: 'Anggota biasa',
    hint: 'Untuk hampir semua orang, termasuk kurir dan staf lapangan. Tidak dapat mengundang.',
  },
  {
    value: 'admin',
    label: 'Admin organisasi',
    hint: 'Dapat mengundang anggota baru. Berikan hanya bila memang perlu.',
  },
];

/** Satu masalah pada satu waktu — layar ini dipakai sambil menyalin alamat dari sumber lain. */
export function inviteProblem(email: string): string | null {
  const bersih = email.trim();
  if (bersih.length === 0) return 'Email belum diisi.';
  if (!bersih.includes('@') || bersih.startsWith('@') || bersih.endsWith('@')) {
    return 'Email tidak terlihat benar.';
  }
  if (/\s/.test(bersih)) return 'Email tidak boleh mengandung spasi.';
  return null;
}

/**
 * Tautan penerimaan yang mendarat di WORKFORCE, bukan di aplikasi saudara.
 *
 * Email undangan yang dikirim HBS memakai `APP_PUBLIC_URL` miliknya sendiri — itu wilayah HBS dan
 * tidak dapat diubah dari sini. Karena itu tautan Workforce dibangun DI SINI dari token yang
 * dikembalikan sekali oleh endpoint undangan, lalu disalin admin dan dikirim lewat WhatsApp.
 *
 * Hasilnya: kurir yang dikirimi tautan ini tidak pernah membuka CRM sama sekali.
 */
export function buildAcceptUrl(origin: string, token: string): string {
  const bersih = origin.replace(/\/+$/, '');
  return `${bersih}/accept?token=${encodeURIComponent(token)}`;
}

/**
 * Token dari parameter alamat — dipangkas dan divalidasi bentuknya.
 *
 * Mengembalikan `null` untuk token kosong ATAU yang hanya berisi spasi: keduanya menghasilkan
 * permintaan yang pasti ditolak server, dan menolaknya di sini memberi pesan yang dapat dibaca
 * alih-alih galat 400 yang berbunyi "validasi gagal".
 */
export function tokenFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const token = (params.get('token') ?? '').trim();
  return token.length > 0 ? token : null;
}

/** Sama dengan syarat HBS (`@MinLength(8)`), diselaraskan sengaja: satu identitas, satu syarat. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Kata sandi untuk AKUN BARU — dan hanya untuk akun baru.
 *
 * HBS mengabaikan kata sandi bila emailnya sudah punya akun. Karena itu kolom ini opsional, dan
 * fungsi ini mengembalikan `null` untuk kata sandi KOSONG: mewajibkannya berarti memaksa orang
 * mengetik sesuatu yang akan dibuang — jebakan yang sudah menggigit dua kali di halaman undangan
 * CRM, dan akibatnya baru muncul saat ia gagal masuk beberapa menit kemudian.
 */
export function newAccountPasswordProblem(password: string, confirmation: string): string | null {
  if (password.length === 0) return null;
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Kata sandi minimal ${MIN_PASSWORD_LENGTH} karakter.`;
  }
  if (password !== confirmation) return 'Kedua kata sandi belum sama.';
  return null;
}
